import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  Tooltip,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`follow-up-tabpanel-${index}`}
      aria-labelledby={`follow-up-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const NewConvertDetail = () => {
  const { id, organizationSlug } = useParams();
  const navigate = useNavigate();
  const { organizationSlug: authOrgSlug } = useAuth();
  const currentOrgSlug = organizationSlug || authOrgSlug;
  const [newConvert, setNewConvert] = useState(null);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [followUpType, setFollowUpType] = useState('phone_call');
  const [contactedBy, setContactedBy] = useState('');
  const [status, setStatus] = useState('contacted');
  const [followUpDate, setFollowUpDate] = useState(new Date());
  const [recommendation, setRecommendation] = useState('');
  const [notes, setNotes] = useState('');
  const [foundationSchool, setFoundationSchool] = useState(false);
  const [joinedWSF, setJoinedWSF] = useState(false);
  const [joinedServiceUnit, setJoinedServiceUnit] = useState(false);
  const [waterBaptism, setWaterBaptism] = useState(false);
  const [holyGhostBaptism, setHolyGhostBaptism] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = JSON.parse(localStorage.getItem('user'));
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch new convert details
        const fetchNewConvertDetails = async (retryCount = 0) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/detail/${id}`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            return response.data;
          } catch (error) {
            console.error('Error fetching new convert details:', error);
            
            // Fall back to the main new-converts endpoint if detail endpoint fails
            if (error.response?.status === 404 && retryCount === 0) {
              console.log('Attempting to fetch from alternate endpoint...');
              try {
                const fallbackResponse = await axios.get(
                  `${process.env.REACT_APP_API_URL}/api/new-converts/${id}`,
                  {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  }
                );
                return fallbackResponse.data;
              } catch (fallbackError) {
                console.error('Fallback endpoint also failed:', fallbackError);
                throw fallbackError;
              }
            }
            
            throw error;
          }
        };
        
        const convertData = await fetchNewConvertDetails();
        setNewConvert(convertData);
        
        // Fetch follow-up history
        try {
          const historyResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/history/${id}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          setFollowUpHistory(historyResponse.data);
        } catch (historyError) {
          console.error('Error fetching follow-up history:', historyError);
          setFollowUpHistory([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.error || 'Failed to load new convert details');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNewFollowUp = async () => {
    try {
      // Always use the POST endpoint to create a new record, even when editing
      const url = `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/${id}`;

      const followUpData = {
        notes,
        status,
        follow_up_type: followUpType,
        contacted_by: contactedBy,
        recommendation,
        follow_up_date: followUpDate,
        completed_foundation_school: foundationSchool,
        joined_wsf: joinedWSF,
        joined_service_unit: joinedServiceUnit,
        done_water_baptism: waterBaptism,
        received_holy_ghost_baptism: holyGhostBaptism
      };

      const response = await axios.post(
        url,
        followUpData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.status === 201 || response.status === 200) {
        handleDialogClose();
        
        // Refresh all data
        await refreshAllData();
        
        setSnackbarMessage('Follow-up saved successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving follow-up:', error);
      setSnackbarMessage('Failed to save follow-up');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Function to refresh all data
  const refreshAllData = async () => {
    try {
      // Refresh follow-up history
      const historyResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/history/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setFollowUpHistory(historyResponse.data);
      
      // Refresh new convert details
      const detailsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/detail/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setNewConvert(detailsResponse.data);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleShowFollowUpDialog = () => {
    setEditingFollowUp(null);
    setFollowUpType('phone_call');
    setContactedBy(`${user?.first_name} ${user?.last_name}`);
    setStatus('contacted');
    setRecommendation('');
    setNotes('');
    setFollowUpDate(new Date());
    setFoundationSchool(false);
    setJoinedWSF(false);
    setJoinedServiceUnit(false);
    setWaterBaptism(false);
    setHolyGhostBaptism(false);
    setShowFollowUpDialog(true);
  };

  const handleEditFollowUp = (followUp) => {
    // Just pre-fill the form with the existing follow-up data
    // but will create a new record rather than updating
    setFollowUpType(followUp.follow_up_type);
    setContactedBy(followUp.contacted_by);
    setStatus(followUp.status);
    setRecommendation(followUp.recommendation);
    setNotes(followUp.notes || '');
    setFollowUpDate(new Date(followUp.follow_up_date));
    setFoundationSchool(followUp.completed_foundation_school);
    setJoinedWSF(followUp.joined_wsf);
    setJoinedServiceUnit(followUp.joined_service_unit);
    setWaterBaptism(followUp.done_water_baptism);
    setHolyGhostBaptism(followUp.received_holy_ghost_baptism);
    setShowFollowUpDialog(true);
  };

  const handleDialogClose = () => {
    setShowFollowUpDialog(false);
    setEditingFollowUp(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/new-converts/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setSnackbarMessage('New convert deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Navigate back after a short delay
      setTimeout(() => {
        const newConvertsPath = currentOrgSlug ? `/${currentOrgSlug}/new-converts` : '/new-converts';
        navigate(newConvertsPath);
      }, 1500);
    } catch (error) {
      console.error('Error deleting new convert:', error);
      setSnackbarMessage('Failed to delete new convert');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!newConvert) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">New convert not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/new-converts')}
          >
            Back to List
          </Button>
          <Stack direction="row" spacing={1}>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={handleShowFollowUpDialog}
            >
              New Follow-up
            </Button>
            <IconButton 
              color="info" 
              onClick={() => navigate(`/email-composer?audienceType=new_converts&recipientEmails=${newConvert?.email || ''}`)}
              aria-label="Send Email"
              title="Send Email"
            >
              <SendIcon />
            </IconButton>
            <IconButton 
              color="error" 
              onClick={handleDeleteClick}
              aria-label="Delete new convert"
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Box>

        <Typography variant="h4" gutterBottom>
          {newConvert.first_name} {newConvert.last_name}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Conversion Type: {newConvert.conversion_type === 'first_time' ? 'First Time Salvation' : 'Rededication to Christ'}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Personal Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Phone Number</Typography>
                    <Typography>{newConvert.phone_number}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Email</Typography>
                    <Typography>{newConvert.email || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Address</Typography>
                    <Typography>
                      {newConvert.street_address}<br />
                      {newConvert.city}, {newConvert.state} {newConvert.postal_code}<br />
                      {newConvert.country}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Additional Information</Typography>
                {newConvert.requires_counselling && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Requires Counselling</Typography>
                    <Typography>Area: {newConvert.counselling_area || 'Not specified'}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2">Prayer Request</Typography>
                  <Typography style={{ whiteSpace: 'pre-wrap' }}>
                    {newConvert.prayer_request || 'None provided'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Follow-up Status</Typography>
                <Typography 
                  variant="subtitle1" 
                  color={
                    newConvert.follow_up_status === 'completed' ? 'success.main' :
                    newConvert.follow_up_status === 'contacted' ? 'info.main' :
                    newConvert.follow_up_status === 'unreachable' ? 'error.main' :
                    'warning.main'
                  }
                  sx={{ mb: 2 }}
                >
                  {newConvert.follow_up_status ? 
                    newConvert.follow_up_status.charAt(0).toUpperCase() + newConvert.follow_up_status.slice(1) 
                    : 'Pending'}
                </Typography>
                {newConvert.last_follow_up_date && (
                  <Typography variant="body2" color="text.secondary">
                    Last followed up: {new Date(newConvert.last_follow_up_date).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Follow-up History</Typography>
                {followUpHistory.length === 0 ? (
                  <Typography color="text.secondary">No follow-up records yet</Typography>
                ) : (
                  <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {followUpHistory.map((record, index) => (
                        <Box 
                          component="li" 
                          key={record.id} 
                          sx={{ 
                            mb: 3,
                            pb: 2,
                            borderBottom: index < followUpHistory.length - 1 ? '1px solid #eee' : 'none',
                            '&:hover': {
                              '& .edit-action': {
                                opacity: 1
                              }
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2">
                              {new Date(record.follow_up_date).toLocaleDateString()} - {record.follow_up_type.replace(/_/g, ' ')}
                              {record.contacted_by_with_unit && 
                                ` by ${record.contacted_by_with_unit}`
                              }
                            </Typography>
                            <IconButton 
                              size="small" 
                              className="edit-action"
                              onClick={() => handleEditFollowUp(record)}
                              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                            >
                              <Tooltip title="Create a new follow-up based on this one">
                                <ContentCopyIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Status: <span style={{
                              color: 
                                record.status === 'completed' ? theme.palette.success.main :
                                record.status === 'contacted' ? theme.palette.info.main :
                                record.status === 'unreachable' ? theme.palette.error.main :
                                theme.palette.warning.main
                            }}>
                              {record.status}
                            </span>
                          </Typography>
                          
                          {/* Growth metrics - only show what's been achieved */}
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {record.completed_foundation_school && (
                              <Chip size="small" color="primary" label="Foundation School" />
                            )}
                            {record.joined_wsf && (
                              <Chip size="small" color="primary" label="WSF" />
                            )}
                            {record.joined_service_unit && (
                              <Chip size="small" color="primary" label="Service Unit" />
                            )}
                            {record.done_water_baptism && (
                              <Chip size="small" color="primary" label="Water Baptism" />
                            )}
                            {record.received_holy_ghost_baptism && (
                              <Chip size="small" color="primary" label="Holy Ghost Baptism" />
                            )}
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Recommendation: {record.recommendation.replace(/_/g, ' ')}
                          </Typography>
                          
                          {record.notes && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mt: 1, 
                                whiteSpace: 'pre-wrap',
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                bgcolor: 'background.paper',
                                p: 1,
                                borderLeft: '3px solid #eee'
                              }}
                            >
                              {record.notes}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Follow-up Dialog */}
        <Dialog 
          open={showFollowUpDialog} 
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            New Follow-up Record
          </DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <DatePicker
                    label="Follow-up Date"
                    value={followUpDate}
                    onChange={(newValue) => {
                      setFollowUpDate(newValue);
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Follow-up Type</FormLabel>
                    <RadioGroup 
                      row 
                      name="follow-up-type" 
                      value={followUpType}
                      onChange={(e) => setFollowUpType(e.target.value)}
                    >
                      <FormControlLabel value="phone_call" control={<Radio />} label="Phone Call" />
                      <FormControlLabel value="email" control={<Radio />} label="Email" />
                      <FormControlLabel value="home_visit" control={<Radio />} label="Home Visit" />
                      <FormControlLabel value="homecell" control={<Radio />} label="Homecell" />
                      <FormControlLabel value="other" control={<Radio />} label="Other" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contacted By"
                    value={contactedBy}
                    onChange={(e) => setContactedBy(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Status</FormLabel>
                    <RadioGroup 
                      row 
                      name="status" 
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <FormControlLabel value="contacted" control={<Radio />} label="Contacted" />
                      <FormControlLabel value="completed" control={<Radio />} label="Completed" />
                      <FormControlLabel value="unreachable" control={<Radio />} label="Unreachable" />
                      <FormControlLabel value="pending" control={<Radio />} label="Pending" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Recommendation</FormLabel>
                    <RadioGroup 
                      row 
                      name="recommendation" 
                      value={recommendation}
                      onChange={(e) => setRecommendation(e.target.value)}
                    >
                      <FormControlLabel value="more_follow_up" control={<Radio />} label="More Follow-up" />
                      <FormControlLabel value="stronger_follow_up" control={<Radio />} label="Stronger Follow-up" />
                      <FormControlLabel value="established" control={<Radio />} label="Established" />
                      <FormControlLabel value="add_to_homecell" control={<Radio />} label="Add to Homecell" />
                      <FormControlLabel value="join_service_group" control={<Radio />} label="Join Service Group" />
                      <FormControlLabel value="non_permanent_visitor" control={<Radio />} label="Non-permanent Visitor" />
                      <FormControlLabel value="not_reachable" control={<Radio />} label="Not Reachable" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Growth Status</Typography>
                  <Grid container>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            checked={foundationSchool} 
                            onChange={(e) => setFoundationSchool(e.target.checked)} 
                          />
                        } 
                        label="Completed Foundation School" 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            checked={joinedWSF} 
                            onChange={(e) => setJoinedWSF(e.target.checked)} 
                          />
                        } 
                        label="Joined WSF" 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            checked={joinedServiceUnit} 
                            onChange={(e) => setJoinedServiceUnit(e.target.checked)} 
                          />
                        } 
                        label="Joined Service Unit" 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            checked={waterBaptism} 
                            onChange={(e) => setWaterBaptism(e.target.checked)} 
                          />
                        } 
                        label="Done Water Baptism" 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            checked={holyGhostBaptism} 
                            onChange={(e) => setHolyGhostBaptism(e.target.checked)} 
                          />
                        } 
                        label="Received Holy Ghost Baptism" 
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter any additional notes about the follow-up here..."
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button onClick={handleNewFollowUp} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the new convert record for{' '}
              {newConvert?.first_name} {newConvert?.last_name}?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default NewConvertDetail; 